import React, { useEffect, useRef, useState } from 'react'
import { WriteMessageForm } from 'components/inbox/WriteMessageForm'
import { ConversationMessageThread } from 'components/inbox/shared/ConversationMessageThread'
import { createInboxMessage } from 'components/inbox/services/createInboxMessage'
import { fetchInboxConversation } from 'components/inbox/services/fetchInboxConversation'
import type { InboxMessage, InboxParticipant, ReviewerDisplayState } from 'components/inbox/inboxTypes'
import type { Review as ReviewType } from 'components/supplier_show/prop_shapes/review'
import { MessageModalLayout } from 'components/inbox/shared/MessageModalLayout'
import { PrivateMessageRecipientsField } from 'components/inbox/message_modals/PrivateMessageRecipientsField'
import type { WriteMessageFormSendResult } from 'components/inbox/WriteMessageForm'
import { formatReviewerRecipientName } from 'components/inbox/utils'

interface ExistingConversationRef {
  id: number
}

interface Props {
  review: ReviewType
  reviewerDisplayState: ReviewerDisplayState
  onClose: () => void
  fetchConversation: (reviewId: number) => Promise<ExistingConversationRef | null>
}

const buildRecipients = (review: ReviewType): InboxParticipant[] => {
  const reviewerFullName = formatReviewerRecipientName(review.author.name, review.is_anonymous === true)

  return [{
    firstName: reviewerFullName,
    fullName: reviewerFullName,
    display: review.is_anonymous === true ? 'anonymous' : 'full',
    orgName: review.author.organization ?? null,
    avatarUrl: review.author.thumbnail
  }]
}

export const PrivateMessageThreadModal: React.FC<Props> = ({
  review,
  reviewerDisplayState,
  fetchConversation,
  onClose
}) => {
  const [conversationExists, setConversationExists] = useState<boolean>(false)
  const [conversationId, setConversationId] = useState<number | null>(null)
  const [existingMessages, setExistingMessages] = useState<InboxMessage[]>([])
  const [isLoadingConversation, setIsLoadingConversation] = useState<boolean>(true)
  const [conversationLoadError, setConversationLoadError] = useState<string | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)
  const [recipients, setRecipients] = useState<InboxParticipant[]>(buildRecipients(review))
  const scrollRegionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setRecipients(buildRecipients(review))
  }, [review])

  useEffect(() => {
    let isMounted = true
    setIsLoadingConversation(true)
    setConversationLoadError(null)
    void fetchConversation(review.id).then(async (conversation) => {
      if (!isMounted) return
      setConversationId(conversation?.id ?? null)
      setConversationExists(conversation != null)

      if (conversation == null) {
        setExistingMessages([])
        return
      }

      try {
        const detail = await fetchInboxConversation(conversation.id)
        if (!isMounted) return
        setExistingMessages(detail.messages)
        setRecipients(detail.participants)
        setConversationLoadError(null)
      } catch {
        if (!isMounted) return
        setExistingMessages([])
        setConversationLoadError('Unable to load previous messages. You can still send a new message.')
      }
    }).catch(() => {
      if (!isMounted) return
      setConversationId(null)
      setConversationExists(false)
      setExistingMessages([])
      setConversationLoadError('Unable to load previous messages. You can still send a new message.')
    }).finally(() => {
      if (!isMounted) return
      setIsLoadingConversation(false)
    })

    return () => {
      isMounted = false
    }
  }, [review.id, fetchConversation])

  useEffect(() => {
    if (scrollRegionRef.current == null || conversationId == null) return
    scrollRegionRef.current.scrollTop = scrollRegionRef.current.scrollHeight
  }, [conversationId, existingMessages.length])

  const handleCreateOrSendMessage = async (
    messageBody: string
  ): Promise<WriteMessageFormSendResult | undefined> => {
    setSendError(null)
    if (isLoadingConversation || conversationId == null) return

    try {
      const message = await createInboxMessage(conversationId, messageBody)
      setExistingMessages((prev) => [...prev, message])
      return 'sent'
    } catch {
      setSendError('Unable to send private message. Please try again.')
    }
  }

  return (
    <MessageModalLayout
      review={review}
      title='Private Message'
      onClose={onClose}
      testId='private-message-modal'
      recipientField={<PrivateMessageRecipientsField participants={recipients} />}
      scrollRegionRef={scrollRegionRef}
      scrollableContent={
        <>
          {isLoadingConversation && (
            <div className='message-reviewer-modal__existing-conversation'>
              Loading conversation…
            </div>
          )}

          {!isLoadingConversation && conversationExists && (
            <div className='message-reviewer-modal__existing-conversation inbox-message-container'>
              <div className='inbox-message-scroll'>
                <ConversationMessageThread messages={existingMessages} />
              </div>
            </div>
          )}
        </>
      }
      footerContent={
        <>
          {conversationLoadError != null && (
            <div className='write-message-error' role='alert'>
              {conversationLoadError}
            </div>
          )}

          {sendError != null && (
            <div className='write-message-error' role='alert'>
              {sendError}
            </div>
          )}

          <WriteMessageForm
            reviewerDisplayState={reviewerDisplayState}
            disableSend={isLoadingConversation}
            onSend={handleCreateOrSendMessage}
            onCancel={onClose}
            showShareWithSupplierCTA={false}
          />
        </>
      }
    />
  )
}
